import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { UserCheck, ArrowRight, CheckCircle2, AlertCircle, KeyRound, Mail, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { useAppConfig } from "../lib/AppConfigContext";
import api from "../lib/api";
import { PasswordStrength } from "../components/PasswordStrength";
import { LegalFooter } from "../components/LegalFooter";

export default function VoterOnlineRegisterPage() {
  const { t, i18n } = useTranslation();
  const { entityId } = useParams();
  const { appName } = useAppConfig();
  const [entityName, setEntityName] = useState("");
  const [entityLogo, setEntityLogo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [blocked, setBlocked] = useState(false);
  const [disabled, setDisabled] = useState(false);

  // Step 1: DNI + Birth Date
  const [dni, setDni] = useState("");
  const [birthDate, setBirthDate] = useState("");
  const [verifying, setVerifying] = useState(false);

  // Step 2: Email + Password
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [registering, setRegistering] = useState(false);

  // Step 3: Success
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    loadEntityInfo();
  }, [entityId]);

  const loadEntityInfo = async () => {
    try {
      const res = await api.get(`/public/entity-registration-info/${entityId}`);
      setEntityName(res.data.entity_name);
      if (res.data.logo_url) setEntityLogo(res.data.logo_url);
      if (res.data.language && res.data.language !== i18n.language) {
        i18n.changeLanguage(res.data.language);
      }
      if (!res.data.online_registration_enabled) setDisabled(true);
    } catch {
      setError(t('errors.invalidLink'));
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    if (!dni.trim() || !birthDate.trim()) {
      toast.error(t('errors.allFieldsRequired'));
      return;
    }

    setVerifying(true);
    try {
      await api.post("/public/voter-online-verify", {
        entity_id: entityId,
        dni: dni.trim(),
        birth_date: birthDate.trim(),
      });
      setStep(2);
    } catch (err) {
      const detail = err.response?.data?.detail || t('censusCheck.verifyError');
      if (err.response?.status === 403) {
        setBlocked(true);
      }
      toast.error(detail);
    } finally {
      setVerifying(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password || !passwordConfirm) {
      toast.error(t('errors.allFieldsRequired'));
      return;
    }
    if (password !== passwordConfirm) {
      toast.error(t('auth.passwordsDoNotMatch'));
      return;
    }
    if (password.length < 10 || !/[A-Z]/.test(password) || !/[a-z]/.test(password) || !/[0-9]/.test(password) || !/[!@#$%^&*()\-_=+\[\]{};:'",.<>?/\\|`~]/.test(password)) {
      toast.error(t('auth.passwordTooWeak'));
      return;
    }

    setRegistering(true);
    try {
      await api.post("/public/voter-online-complete", {
        entity_id: entityId,
        dni: dni.trim(),
        birth_date: birthDate.trim(),
        email: email.trim(),
        password,
      });
      setSuccess(true);
      setStep(3);
    } catch (err) {
      const detail = err.response?.data?.detail || t('onlineRegister.registerError');
      toast.error(detail);
    } finally {
      setRegistering(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-4 border-blue-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <p className="text-lg font-medium text-slate-900">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (disabled) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full space-y-6 text-center">
          {entityLogo && (
            <img src={entityLogo} alt={entityName} className="h-16 w-auto mx-auto object-contain" />
          )}
          <Card>
            <CardContent className="pt-6 text-center">
              <AlertCircle className="h-12 w-12 text-amber-500 mx-auto mb-4" />
              <h2 className="text-lg font-bold text-slate-900 mb-2">{t('onlineRegister.notAvailable')}</h2>
              <p className="text-slate-600" dangerouslySetInnerHTML={{ __html: t('onlineRegister.notActiveMsg', { entity: entityName }) }} />
            </CardContent>
          </Card>
          <p className="text-xs text-slate-400">{appName} - {t('common.secureVotingSystem')}</p>
          <LegalFooter />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4" data-testid="voter-online-register-page">
      <div className="max-w-md w-full space-y-6">
        {/* Header */}
        <div className="text-center">
          {entityLogo && (
            <img
              src={entityLogo}
              alt={entityName}
              className="h-16 w-auto mx-auto mb-3 object-contain"
              data-testid="entity-logo"
            />
          )}
          <h1 className="text-2xl font-bold text-slate-900">{appName}</h1>
          <p className="text-slate-500 mt-1">{t('onlineRegister.title')} - {entityName}</p>
        </div>

        {/* Steps indicator */}
        <div className="flex items-center justify-center gap-2">
          {[1, 2, 3].map((s) => (
            <div key={s} className="flex items-center gap-2">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  step >= s
                    ? "bg-blue-600 text-white"
                    : "bg-slate-200 text-slate-500"
                }`}
              >
                {step > s ? <CheckCircle2 className="h-4 w-4" /> : s}
              </div>
              {s < 3 && <div className={`w-12 h-0.5 ${step > s ? "bg-blue-600" : "bg-slate-200"}`} />}
            </div>
          ))}
        </div>

        {/* Blocked message */}
        {blocked && (
          <Card className="border-red-200 bg-red-50">
            <CardContent className="pt-6 text-center">
              <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
              <h2 className="text-lg font-bold text-red-900 mb-2">{t('onlineRegister.dniBlocked')}</h2>
              <p className="text-red-700">
                {t('onlineRegister.dniBlockedDesc')}
              </p>
            </CardContent>
          </Card>
        )}

        {/* Step 1: Identification */}
        {step === 1 && !blocked && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <UserCheck className="h-5 w-5 text-blue-600" />
                {t('onlineRegister.identification')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleVerify} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="dni">DNI / NIF</Label>
                  <Input
                    id="dni"
                    data-testid="online-reg-dni"
                    placeholder="12345678A"
                    value={dni}
                    onChange={(e) => setDni(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="birthDate">{t('censusCheck.birthDateLabel')}</Label>
                  <Input
                    id="birthDate"
                    data-testid="online-reg-birthdate"
                    type="date"
                    value={birthDate}
                    onChange={(e) => setBirthDate(e.target.value)}
                  />
                </div>
                <Button
                  type="submit"
                  disabled={verifying}
                  className="w-full bg-blue-600 hover:bg-blue-700"
                  data-testid="online-reg-verify-btn"
                >
                  {verifying ? (
                    <span className="flex items-center gap-2">
                      <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                      {t('onlineRegister.verifying')}
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      {t('onlineRegister.continue')}
                      <ArrowRight className="h-4 w-4" />
                    </span>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Step 2: Email + Password */}
        {step === 2 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <KeyRound className="h-5 w-5 text-blue-600" />
                {t('onlineRegister.createPassword')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0" />
                <p className="text-sm text-green-800">{t('onlineRegister.inCensusComplete')}</p>
              </div>
              <form onSubmit={handleRegister} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">{t('common.email')}</Label>
                  <Input
                    id="email"
                    data-testid="online-reg-email"
                    type="email"
                    placeholder="el-teu@correu.cat"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password">{t('censusCheck.passwordLabel')}</Label>
                  <Input
                    id="password"
                    data-testid="online-reg-password"
                    type="password"
                    placeholder={t('passwordPolicy.minLength', { count: 10 })}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                  <PasswordStrength password={password} minLength={10} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="passwordConfirm">{t('onlineRegister.repeatPassword')}</Label>
                  <Input
                    id="passwordConfirm"
                    data-testid="online-reg-password-confirm"
                    type="password"
                    placeholder={t('onlineRegister.repeatPassword')}
                    value={passwordConfirm}
                    onChange={(e) => setPasswordConfirm(e.target.value)}
                  />
                </div>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setStep(1)}
                    data-testid="online-reg-back-btn"
                  >
                    <ArrowLeft className="h-4 w-4 mr-1" />
                    {t('onlineRegister.back')}
                  </Button>
                  <Button
                    type="submit"
                    disabled={registering}
                    className="flex-1 bg-green-600 hover:bg-green-700"
                    data-testid="online-reg-submit-btn"
                  >
                    {registering ? (
                      <span className="flex items-center gap-2">
                        <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                        {t('common.processing')}
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        <CheckCircle2 className="h-4 w-4" />
                        {t('onlineRegister.completeRegister')}
                      </span>
                    )}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Step 3: Success */}
        {step === 3 && (
          <Card>
            <CardContent className="pt-6 text-center">
              <CheckCircle2 className="h-16 w-16 text-green-600 mx-auto mb-4" />
              <h2 className="text-xl font-bold text-slate-900 mb-2">{t('onlineRegister.completed')}</h2>
              <p className="text-slate-600" dangerouslySetInnerHTML={{ __html: t('onlineRegister.successMsg', { entity: entityName }) }} />
            </CardContent>
          </Card>
        )}

        {/* Footer */}
        <p className="text-center text-xs text-slate-400">
          {appName} - {t('common.secureVotingSystem')}
        </p>
        <LegalFooter />
      </div>
    </div>
  );
}
